import React, { useState, useRef, useEffect } from 'react';
import { X, Send, Loader2, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import api from '../lib/api';

// DenAIs custom icon - A cool character with spiky hair and a cigarette
const DenAIsIcon = ({ className = "w-7 h-7" }) => (
  <svg className={className} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Head/Face */}
    <circle cx="24" cy="22" r="12" fill="#FCD34D" stroke="#F59E0B" strokeWidth="1.5"/>
    {/* Spiky hair - brush style */}
    <path d="M14 14 L16 8 L18 13" fill="#4B5563"/>
    <path d="M17 12 L19 5 L21 11" fill="#374151"/>
    <path d="M20 10 L22 3 L24 9" fill="#4B5563"/>
    <path d="M23 9 L25 2 L27 8" fill="#374151"/>
    <path d="M26 10 L28 4 L30 10" fill="#4B5563"/>
    <path d="M29 11 L31 6 L33 12" fill="#374151"/>
    <path d="M32 13 L34 9 L35 14" fill="#4B5563"/>
    {/* Sunglasses */}
    <rect x="14" y="19" width="7" height="5" rx="1" fill="#1F2937"/>
    <rect x="27" y="19" width="7" height="5" rx="1" fill="#1F2937"/>
    <rect x="21" y="20" width="6" height="2" fill="#374151"/>
    {/* Slight smirk */}
    <path d="M20 29 Q24 31 28 29" stroke="#92400E" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
    {/* Cigarette */}
    <rect x="28" y="28" width="14" height="3" rx="1" fill="#F5F5F4"/>
    <rect x="28" y="28" width="3" height="3" rx="0.5" fill="#FDE68A"/>
    {/* Smoke wisps */}
    <path d="M44 26 Q46 24 44 22 Q46 20 44 18" stroke="#D1D5DB" strokeWidth="1" strokeLinecap="round" fill="none" opacity="0.7"/>
    <path d="M42 24 Q44 21 42 19" stroke="#E5E7EB" strokeWidth="0.8" strokeLinecap="round" fill="none" opacity="0.5"/>
    {/* Body hint - shoulders */}
    <path d="M12 36 Q24 40 36 36" stroke="#4B5563" strokeWidth="3" strokeLinecap="round" fill="none"/>
  </svg>
);

const AIChatWidget = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [showBubble, setShowBubble] = useState(true);  // Show welcome bubble
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: null  // Will be set with translation
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const scrollRef = useRef(null);
  const messagesEndRef = useRef(null);  // Reference to scroll to
  const inputRef = useRef(null);

  // Set initial message with translation
  useEffect(() => {
    setMessages([{
      role: 'assistant',
      content: t('chat_welcome_message', 'Ciao, sono DenAIs, il tuo assistente. Scrivimi se hai bisogno!\n\n• Come usare l\'app\n• Caricamento disegni\n• Creazione marker\n• Funzioni AI')
    }]);
  }, [t]);

  // Hide bubble after 30 seconds or when chat is opened
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowBubble(false);
    }, 30000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setShowBubble(false);
    }
  }, [isOpen]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    // Use scrollIntoView for better cross-browser compatibility
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isLoading]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await api.post('/ai-chat', { message: userMessage });
      setMessages(prev => [...prev, { role: 'assistant', content: response.data.reply }]);
    } catch (error) {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Mi dispiace, si è verificato un errore. Riprova tra qualche istante.' 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Welcome Bubble */}
      {showBubble && !isOpen && (
        <div 
          className="fixed bottom-24 right-6 z-50 animate-in fade-in slide-in-from-bottom-3 duration-500"
          onClick={() => setIsOpen(true)}
        >
          <div className="relative bg-white rounded-2xl shadow-lg border border-slate-200 p-4 max-w-[220px] cursor-pointer hover:shadow-xl transition-shadow">
            <div className="text-sm text-slate-700">
              {t('chat_bubble_message', '👋 Hai bisogno di aiuto? Chiedimi!')}
            </div>
            {/* Arrow pointing to button */}
            <div className="absolute -bottom-2 right-8 w-4 h-4 bg-white border-r border-b border-slate-200 transform rotate-45"></div>
            {/* Close button */}
            <button 
              className="absolute -top-2 -right-2 w-5 h-5 bg-slate-200 hover:bg-slate-300 rounded-full flex items-center justify-center text-slate-500"
              onClick={(e) => {
                e.stopPropagation();
                setShowBubble(false);
              }}
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}

      {/* Chat Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 print:hidden ${
          isOpen 
            ? 'bg-slate-700 hover:bg-slate-800' 
            : 'bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700'
        }`}
        data-testid="ai-chat-button"
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <DenAIsIcon className="w-9 h-9" />
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div 
          className="fixed bottom-24 right-6 z-50 w-80 sm:w-96 h-[500px] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden animate-in slide-in-from-bottom-5 duration-300 print:hidden"
          data-testid="ai-chat-window"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-amber-500 to-orange-600 p-4 text-white">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center overflow-hidden">
                <DenAIsIcon className="w-9 h-9" />
              </div>
              <div>
                <h3 className="font-semibold">DenAIs</h3>
                <p className="text-xs text-white/80">{t('denais_subtitle', 'Il tuo assistente SmartBalloon')}</p>
              </div>
            </div>
          </div>

          {/* Messages */}
          <ScrollArea className="flex-1 p-4" ref={scrollRef}>
            <div className="space-y-4">
              {messages.map((msg, i) => (
                <div
                  key={i}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] p-3 rounded-2xl text-sm whitespace-pre-wrap ${
                      msg.role === 'user'
                        ? 'bg-blue-500 text-white rounded-br-md'
                        : 'bg-slate-100 text-slate-800 rounded-bl-md'
                    }`}
                  >
                    {msg.content}
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-slate-100 p-3 rounded-2xl rounded-bl-md">
                    <Loader2 className="w-5 h-5 animate-spin text-slate-500" />
                  </div>
                </div>
              )}
              {/* Invisible element to scroll to */}
              <div ref={messagesEndRef} />
            </div>
          </ScrollArea>

          {/* Input */}
          <div className="p-3 border-t border-slate-200 bg-slate-50">
            <div className="flex gap-2">
              <Input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Scrivi un messaggio..."
                className="flex-1 bg-white"
                disabled={isLoading}
                data-testid="ai-chat-input"
              />
              <Button 
                onClick={sendMessage} 
                disabled={!input.trim() || isLoading}
                size="icon"
                className="bg-blue-500 hover:bg-blue-600"
                data-testid="ai-chat-send"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-[10px] text-slate-400 mt-2 text-center">
              Assistente AI gratuito per supporto app
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default AIChatWidget;
